import { createLivekitContextEngine } from './createLivekitContextEngine'
import type {
  EventsExportItem,
  EventsExport,
  TranscriptExport,
  TranscriptExportItem,
  LivekitTimerFormatter,
} from './types'

type LivekitContextEngine = ReturnType<typeof createLivekitContextEngine>

const buildTranscriptItems = (engine: LivekitContextEngine): TranscriptExportItem[] =>
  engine.transcripts.value.flatMap((group) =>
    group.segments.map((segment) => ({
      segmentId: segment.segment_id ?? segment.id,
      speaker: group.is_external ? ('client' as const) : ('agent' as const),
      text: segment.text,
      startOffsetSec: segment.start_time,
      endOffsetSec: segment.end_time,
      alerts: group.segmentsStatuses?.map((item) => item.text) ?? [],
      toolCalls: segment.tools_called ?? [],
    })),
  )

const buildEventItems = (
  engine: LivekitContextEngine,
  timerFormatter: LivekitTimerFormatter,
): EventsExportItem[] => {
  const items: {
    id: EventsExportItem['id']
    kind: EventsExportItem['kind']
    time: EventsExportItem['time']
    label: EventsExportItem['label']
    detail: EventsExportItem['detail']
    sortSeconds: number
    sortIndex: number
  }[] = []
  let sortIndex = 0

  for (const group of engine.allTranscripts.value) {
    for (const segment of group.segments) {
      for (const toolCall of segment.tools_called ?? []) {
        items.push({
          id: toolCall.id,
          kind: 'tool',
          time: timerFormatter.formatTimerDuration({
            value: segment.start_time,
            alwaysIncludeHours: true,
          }),
          label: toolCall.name,
          detail: toolCall.result?.side_effect_message ?? toolCall.result?.failure_msg ?? '',
          sortSeconds: segment.start_time,
          sortIndex: sortIndex++,
        })
      }
    }
  }

  for (const event of engine.lifecycleEvents.value) {
    const relativeSeconds = engine.getRelativeSeconds(event.timestamp)

    items.push({
      id: `lifecycle-${event.type}-${String(event.timestamp)}`,
      kind: 'lifecycle',
      time: timerFormatter.formatTimerDuration({
        value: relativeSeconds,
        alwaysIncludeHours: true,
      }),
      label: event.type,
      detail: '',
      sortSeconds: relativeSeconds,
      sortIndex: sortIndex++,
    })
  }

  return items
    .sort((left, right) => left.sortSeconds - right.sortSeconds || left.sortIndex - right.sortIndex)
    .map(({ sortSeconds: _sortSeconds, sortIndex: _sortIndex, ...item }) => item)
}

/**
 * Builds the transcript export block from a hydrated engine.
 *
 * Preserves livekit UI transcript semantics, then flattens groups into export
 * items and computes the aggregate alert count expected by the JSON payload.
 */
export const buildTranscriptExport = (engine: LivekitContextEngine): TranscriptExport => {
  const items = buildTranscriptItems(engine)

  return {
    items,
    alertCount: items.reduce((sum, item) => sum + item.alerts.length, 0),
  }
}

/**
 * Builds the events export block from a hydrated engine.
 *
 * Uses engine-normalized tool and lifecycle state so event timing stays aligned
 * with UI-relative transcript timing without reinterpreting raw context again.
 */
export const buildEventsExport = (
  engine: LivekitContextEngine,
  timerFormatter: LivekitTimerFormatter,
): EventsExport => ({
  items: buildEventItems(engine, timerFormatter),
})
